import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  ShoppingCart,
  DollarSign,
  Pause,
  Layers,
  Sparkles,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import RevealPhase from "./RevealPhase";

/**
 * Renders the main center-area action UI for the current turn phase.
 *
 *   decision     → Buy / Sell / Hold (+ Double Investment badge, hint)
 *   action_event → Draw Effect (or skip if a held card was already played)
 *   discard      → Hand-limit prompt
 *   reveal       → Delegates to <RevealPhase/>
 *
 * Everything is driven by the `game` / `currentPlayer` props plus the
 * parent's `doAction` dispatcher — no local state.
 */
function PhaseActions({
  game,
  phase,
  currentPlayer,
  loading,
  selectedSellCard,
  doAction,
  playSound,
  hasPendingChoice,
}) {
  const { t } = useTranslation();
  if (phase === "decision") {
    const hasHoldable = currentPlayer.hand?.some((c) => !c.persistent_symbol);
    return (
      <div className="text-center" data-testid="decision-phase">
        <p
          className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-4"
          style={{ fontFamily: "Fredoka" }}
        >
          {t("phaseActions.decision.prompt", { name: currentPlayer.name })}
        </p>
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
          <Button
            data-testid="buy-btn"
            className="action-btn rounded-full px-4 sm:px-8 py-3 bg-[#00E676] hover:bg-[#00C853] text-white text-base sm:text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px]"
            onClick={() => doAction({ action: "buy" })}
            disabled={loading}
          >
            <ShoppingCart className="w-5 h-5 mr-1.5 sm:mr-2" /> {t("phaseActions.decision.buy")}
          </Button>
          <Button
            data-testid="sell-btn"
            className="action-btn rounded-full px-4 sm:px-8 py-3 bg-[#FF5A5F] hover:bg-[#E53935] text-white text-base sm:text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px]"
            onClick={() => {
              if (selectedSellCard) {
                doAction({ action: "sell", card_id: selectedSellCard });
              } else {
                toast.warning(t("phaseActions.decision.toastSelectCrypto"));
              }
            }}
            disabled={loading || !currentPlayer.portfolio.length}
          >
            <DollarSign className="w-5 h-5 mr-1.5 sm:mr-2" /> {t("phaseActions.decision.sell")}
          </Button>
          <Button
            data-testid="hold-btn"
            className="action-btn rounded-full px-4 sm:px-8 py-3 bg-[#FFC107] hover:bg-[#FFB300] text-[#1A237E] dark:text-[#E3F2FD] text-base sm:text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px]"
            onClick={() => doAction({ action: "hold" })}
            disabled={loading}
          >
            <Pause className="w-5 h-5 mr-1.5 sm:mr-2" /> {t("phaseActions.decision.hold")}
          </Button>
        </div>
        <div className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50 mt-3 space-y-1">
          {currentPlayer.double_investment && (
            <div
              className="inline-flex items-center gap-1.5 bg-[#8247E5]/10 text-[#8247E5] font-bold text-[11px] rounded-full px-3 py-1 border border-[#8247E5]/30 animate-bounce-in"
              data-testid="active-effect-discount"
            >
              <Sparkles className="w-3 h-3" /> {t("phaseActions.decision.discount")}
            </div>
          )}
          <p>
            {t("phaseActions.decision.selectHint")}
            {hasHoldable && !currentPlayer.played_held_this_turn && (
              <span
                className="block mt-1 text-[#8247E5] font-semibold"
                data-testid="hand-play-hint"
              >
                {t("phaseActions.decision.handHint")}
              </span>
            )}
          </p>
        </div>
      </div>
    );
  }

  if (phase === "action_event") {
    if (currentPlayer.played_held_this_turn) {
      return (
        <div className="text-center" data-testid="action-event-phase">
          <p
            className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-2"
            style={{ fontFamily: "Fredoka" }}
          >
            {t("phaseActions.actionEvent.alreadyPlayed")}
          </p>
          <p className="text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-4">
            {t("phaseActions.actionEvent.alreadyPlayedSub")}
          </p>
          <Button
            data-testid="skip-draw-btn"
            className="action-btn rounded-full px-8 py-3 bg-[#38B6FF] hover:bg-[#2196F3] text-white text-lg"
            onClick={() => doAction({ action: "draw_effect" })}
            disabled={loading}
          >
            <ChevronRight className="w-5 h-5 mr-2" /> {t("phaseActions.actionEvent.continue")}
          </Button>
        </div>
      );
    }

    if (currentPlayer.hand.some((c) => !c.persistent_symbol)) {
      return (
        <div className="text-center" data-testid="action-event-phase">
          <p
            className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-4"
            style={{ fontFamily: "Fredoka" }}
          >
            {t("phaseActions.actionEvent.choose")}
          </p>
          <div className="flex gap-4 justify-center">
            <Button
              data-testid="draw-effect-btn"
              className="action-btn rounded-full px-8 py-3 bg-[#38B6FF] hover:bg-[#2196F3] text-white text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px]"
              onClick={() => doAction({ action: "draw_effect" })}
              disabled={loading || game.effect_deck_count === 0}
            >
              <Layers className="w-5 h-5 mr-2" /> {t("phaseActions.actionEvent.drawEffect")}
            </Button>
          </div>
          <p className="text-xs text-[#FF5A5F]/70 mt-3 font-semibold">
            {t("phaseActions.actionEvent.warning")}
          </p>
        </div>
      );
    }

    return (
      <div className="text-center" data-testid="action-event-phase">
        <p
          className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-4"
          style={{ fontFamily: "Fredoka" }}
        >
          {t("phaseActions.actionEvent.drawEffectTitle")}
        </p>
        <Button
          data-testid="draw-effect-btn"
          className="action-btn rounded-full px-8 py-3 bg-[#38B6FF] hover:bg-[#2196F3] text-white text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px]"
          onClick={() => doAction({ action: "draw_effect" })}
          disabled={loading || game.effect_deck_count === 0}
        >
          <Layers className="w-5 h-5 mr-2" /> {t("phaseActions.actionEvent.drawEffect")}
        </Button>
      </div>
    );
  }

  if (phase === "discard") {
    return (
      <div className="text-center" data-testid="discard-phase">
        <AlertTriangle className="w-10 h-10 text-[#FF5A5F] mx-auto mb-2" />
        <p
          className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-2"
          style={{ fontFamily: "Fredoka" }}
        >
          {t("phaseActions.discard.title")}
        </p>
        <p className="text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60">
          {t("phaseActions.discard.subtitle")}
        </p>
      </div>
    );
  }

  // Never show the reveal/Continue panel while a choice is pending - a stale
  // reveal on top of a pending choice lets the user dispatch `continue` and
  // wedge the turn. The ChoiceModal takes precedence; the panel returns once
  // the choice resolves.
  if (phase === "reveal" && hasPendingChoice) {
    return null;
  }

  if (phase === "reveal") {
    return (
      <RevealPhase
        game={game}
        loading={loading}
        onContinue={() => {
          playSound("swoosh");
          doAction({ action: "continue" });
        }}
      />
    );
  }

  return null;
}

export default memo(PhaseActions);
